import { normalizedHpDisplay, unitTotalHp, type Unit } from "@dailywars/core/engine";

import type { CanvasBoardAnimation } from "#/game/animation/types";
import { getCargoBadgeImage, getHpBadgeImage, isHpBadgeValue } from "#/game/badgeSprites";
import { isImageReady } from "#/game/imageReady";

import { TILE_SIZE } from "./boardConstants";
import type { UnitDrawLayout } from "./boardUnitGeometry";

function drawBadgeOverlay({
  ctx,
  img,
  layout,
}: {
  ctx: CanvasRenderingContext2D;
  img: HTMLImageElement;
  layout: UnitDrawLayout;
}): void {
  if (!isImageReady(img)) return;
  ctx.drawImage(img, layout.drawX, layout.drawY, TILE_SIZE, TILE_SIZE);
}

function unitForHpBadge({
  unit,
  animation,
}: {
  unit: Unit;
  animation: CanvasBoardAnimation | null;
}): Unit {
  if (animation?.type !== "move" && animation?.type !== "attackFx") return unit;
  const override = animation.hpDisplayUnits?.find((u) => u.id === unit.id);
  return override ?? unit;
}

function drawUnitHpBadge({
  ctx,
  unit,
  layout,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  layout: UnitDrawLayout;
}): void {
  const hpDisplay = normalizedHpDisplay(unitTotalHp(unit));
  if (!isHpBadgeValue(hpDisplay)) return;
  drawBadgeOverlay({ ctx, img: getHpBadgeImage(hpDisplay), layout });
}

function drawUnitCargoBadge({
  ctx,
  unit,
  layout,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  layout: UnitDrawLayout;
}): void {
  if (!unit.cargo) return;
  drawBadgeOverlay({ ctx, img: getCargoBadgeImage(), layout });
}

export function drawUnitBadges({
  ctx,
  unit,
  layout,
  animation,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  layout: UnitDrawLayout;
  animation: CanvasBoardAnimation | null;
}): void {
  drawUnitHpBadge({ ctx, unit: unitForHpBadge({ unit, animation }), layout });
  drawUnitCargoBadge({ ctx, unit, layout });
}
